import * as React from 'react';
import { cn } from '@/lib/utils';

export interface CDEditableDateProps {
  /** The date value (string format like "4/15/2013" or "MM/DD/YYYY") */
  value: string | null | undefined;
  /** Whether this is an editable field */
  editable?: boolean;
  /** onChange handler for editable mode */
  onChange?: (value: string) => void;
  /** Placeholder text for empty values */
  placeholder?: string;
  /** Size variant */
  size?: 'tiny' | 'small' | 'body';
  /** Whether the text should be bold */
  bold?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * CDEditableDate - Editable date field component
 *
 * Displays a date that can be edited when in editable mode.
 * Uses text input for flexibility with various date formats used in CD forms.
 */
export const CDEditableDate = React.forwardRef<HTMLSpanElement, CDEditableDateProps>(
  (
    {
      value,
      editable = false,
      onChange,
      placeholder = 'MM/DD/YYYY',
      size = 'tiny',
      bold = false,
      className,
    },
    ref,
  ) => {
    const displayValue = value || placeholder;

    const sizeClass = {
      tiny: 'cd-text-tiny',
      small: 'cd-text-small',
      body: 'cd-text-body',
    }[size];

    if (editable) {
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          className={cn('cd-input cd-input-date w-full', sizeClass, bold && 'font-bold', className)}
        />
      );
    }

    return (
      <span
        ref={ref}
        className={cn(sizeClass, bold && 'font-bold', !value && 'text-cd-gray-text', className)}
      >
        {displayValue}
      </span>
    );
  },
);

CDEditableDate.displayName = 'CDEditableDate';
